function normalizeAnalyticsValue(value) {
  if (typeof value !== "string") return "";
  return value.trim();
}

export function parseTripDate(dateStr) {
  if (!dateStr || typeof dateStr !== "string") return null;

  const parts = dateStr.split("-").map(Number);
  if (parts.length !== 3 || parts.some((value) => !Number.isFinite(value))) {
    return null;
  }

  return new Date(parts[0], parts[1] - 1, parts[2]);
}

export function isVideoMedia(mediaUrl = "") {
  return /\.(mp4|mov|webm|m4v|avi|mkv)$/i.test(mediaUrl);
}

const PAST_ANALYTICS_FILTER_KEYS = [
  "species",
  "wayOfFishing",
  "typeOfFishing",
  "style",
  "technique",
  "bait",
  "lure",
  "timeBucket",
];

export const DEFAULT_PAST_ANALYTICS_FILTERS = {
  species: "",
  wayOfFishing: "",
  typeOfFishing: "",
  style: "",
  technique: "",
  bait: "",
  lure: "",
  timeBucket: "",
};

const TIME_BUCKET_META = {
  early_morning: { labelKey: "trip_time_bucket_early_morning", order: 0 },
  morning: { labelKey: "trip_time_bucket_morning", order: 1 },
  afternoon: { labelKey: "trip_time_bucket_afternoon", order: 2 },
  evening: { labelKey: "trip_time_bucket_evening", order: 3 },
  night: { labelKey: "trip_time_bucket_night", order: 4 },
  unknown: { labelKey: "unknown", order: 5 },
};

export function buildAvailableTimeBucketOptions(events) {
  const buckets = new Map();

  (Array.isArray(events) ? events : []).forEach((event) => {
    const bucketId = normalizeAnalyticsValue(event?.timeBucket) || "unknown";
    if (!TIME_BUCKET_META[bucketId] || buckets.has(bucketId)) return;

    buckets.set(bucketId, {
      value: bucketId,
      labelKey: TIME_BUCKET_META[bucketId].labelKey,
      order: TIME_BUCKET_META[bucketId].order,
    });
  });

  return Array.from(buckets.values()).sort((a, b) => a.order - b.order);
}

export function filterPastAnalyticsEvents(events, filters) {
  const rows = Array.isArray(events) ? events : [];

  return rows.filter((event) =>
    PAST_ANALYTICS_FILTER_KEYS.every((key) => {
      const selectedValue = normalizeAnalyticsValue(filters?.[key]);
      if (!selectedValue) return true;

      const eventValue =
        key === "timeBucket"
          ? normalizeAnalyticsValue(event?.timeBucket) || "unknown"
          : normalizeAnalyticsValue(event?.[key]);

      return eventValue.toLowerCase() === selectedValue.toLowerCase();
    })
  );
}

export function buildPastAnalyticsRows(events, view) {
  const rows = Array.isArray(events) ? events : [];

  if (view === "timeBucket") {
    const grouped = new Map();

    rows.forEach((event) => {
      const bucketId = normalizeAnalyticsValue(event?.timeBucket) || "unknown";
      const bucketMeta = TIME_BUCKET_META[bucketId] || TIME_BUCKET_META.unknown;
      const existing = grouped.get(bucketId);

      if (existing) {
        existing.count += 1;
        return;
      }

      grouped.set(bucketId, {
        key: bucketId,
        labelKey: bucketMeta.labelKey,
        count: 1,
        order: bucketMeta.order,
      });
    });

    return Array.from(grouped.values()).sort((a, b) => a.order - b.order);
  }

  if (view === "speciesByDay") {
    const grouped = new Map();

    rows.forEach((event) => {
      const tripDate = normalizeAnalyticsValue(event?.tripDate);
      const species = normalizeAnalyticsValue(event?.species);
      if (!tripDate || !species) return;

      const key = `${tripDate}__${species.toLowerCase()}`;
      const existing = grouped.get(key);

      if (existing) {
        existing.count += 1;
        return;
      }

      const tripDay = Number.isFinite(event?.tripDay) ? event.tripDay : null;
      grouped.set(key, {
        key,
        label: tripDay ? `Day ${tripDay} - ${species}` : species,
        sublabel: tripDate,
        count: 1,
        sortDate: tripDate,
        sortSpecies: species.toLowerCase(),
      });
    });

    return Array.from(grouped.values()).sort((a, b) => {
      if (a.sortDate !== b.sortDate) return a.sortDate.localeCompare(b.sortDate);
      return a.sortSpecies.localeCompare(b.sortSpecies);
    });
  }

  const grouped = new Map();

  rows.forEach((event) => {
    const tripDate = normalizeAnalyticsValue(event?.tripDate);
    if (!tripDate) return;

    const existing = grouped.get(tripDate);
    if (existing) {
      existing.count += 1;
      return;
    }

    const tripDay = Number.isFinite(event?.tripDay) ? event.tripDay : null;
    grouped.set(tripDate, {
      key: tripDate,
      label: tripDay ? `Day ${tripDay}` : tripDate,
      sublabel: tripDate,
      count: 1,
      sortDate: tripDate,
    });
  });

  return Array.from(grouped.values()).sort((a, b) => a.sortDate.localeCompare(b.sortDate));
}

function toIsoDateString(dateObj) {
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, "0");
  const day = String(dateObj.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function buildPastConditionsTimeline(startDate, endDate, conditionDays) {
  const days = Array.isArray(conditionDays) ? conditionDays : [];
  const dayMap = new Map(
    days
      .filter((day) => day && typeof day.date === "string" && day.date)
      .map((day) => [day.date, day])
  );

  const start = parseTripDate(startDate);
  const end = parseTripDate(endDate || startDate);

  if (!start || !end || end < start) {
    return Array.from(dayMap.values()).sort((a, b) => a.date.localeCompare(b.date));
  }

  const timeline = [];
  const cursor = new Date(start);

  while (cursor <= end) {
    const dateKey = toIsoDateString(cursor);
    timeline.push(dayMap.get(dateKey) || { date: dateKey });
    cursor.setDate(cursor.getDate() + 1);
  }

  return timeline;
}

export function toFiniteNumber(value) {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

export function formatPastConditionDateLabel(dateStr) {
  const parsed = parseTripDate(dateStr);
  if (!parsed) return dateStr || "";

  return parsed.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

export function formatPastConditionValue(value, precision, unit) {
  if (!Number.isFinite(value)) return "";

  const normalized =
    precision === 0 ? Math.round(value) : Number(value.toFixed(precision));

  return `${normalized}${unit}`;
}

function parsePastCatchTimelinePoint(dateStr, timeStr) {
  const parsedDate = parseTripDate(dateStr);
  if (!parsedDate) return null;

  const normalizedTime =
    typeof timeStr === "string" ? timeStr.trim() : "";
  const timeMatch = normalizedTime.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);

  if (!timeMatch) {
    parsedDate.setHours(0, 0, 0, 0);
    return {
      timestamp: parsedDate.getTime(),
      isDateOnly: true,
    };
  }

  const hours = Number(timeMatch[1]);
  const minutes = Number(timeMatch[2]);
  const seconds = Number(timeMatch[3] || 0);

  if (
    !Number.isFinite(hours) ||
    !Number.isFinite(minutes) ||
    !Number.isFinite(seconds)
  ) {
    return null;
  }

  parsedDate.setHours(hours, minutes, seconds, 0);
  return {
    timestamp: parsedDate.getTime(),
    isDateOnly: false,
  };
}

export function buildPastCatchTimelineBuckets(events) {
  const grouped = new Map();

  (Array.isArray(events) ? events : []).forEach((event) => {
    const point = parsePastCatchTimelinePoint(
      event?.savedCatchDate || event?.tripDate || "",
      event?.savedCatchTime || ""
    );

    if (!point) return;

    const tripDate = normalizeAnalyticsValue(
      event?.savedCatchDate || event?.tripDate
    );
    const tripTime = normalizeAnalyticsValue(event?.savedCatchTime);
    const key = point.isDateOnly ? tripDate : `${tripDate}T${tripTime}`;

    if (!key) return;

    const existing = grouped.get(key);
    if (existing) {
      existing.count += 1;
      return;
    }

    grouped.set(key, {
      key,
      timestamp: point.timestamp,
      count: 1,
      label: point.isDateOnly && tripDate ? tripDate : `${tripDate} ${tripTime}`.trim(),
    });
  });

  let runningTotal = 0;

  return Array.from(grouped.values())
    .sort((a, b) => a.timestamp - b.timestamp)
    .map((bucket) => {
      runningTotal += bucket.count;
      return {
        ...bucket,
        cumulativeCount: runningTotal,
      };
    });
}
